"use client";

import React, { createContext, useState, useContext, type ReactNode, useEffect, useCallback, useRef, useMemo } from "react";
import type { Project } from "@/features/projects/types";
import { usePhase } from "@/features/projects/hooks/use-phase";
import { useFetchSuggestions } from "@/hooks/use-fetch-suggestion";
import type { Suggestion } from "@/domain/dto/suggestion.dto";
import type { Content } from "@tiptap/core";
import { useStorage, StorageType } from "@/features/projects/hooks/use-storage";

interface ProjectContextType {
  project: Project | null;
  setProject: (project: Project | null) => void;
  selectedPhase: string;
  setSelectedPhase: (phase: string) => void;
  isContinuousImprovement: boolean;
  isProjectInitialized: boolean;
  suggestions: Suggestion[];
  updatePhaseContent: (phaseName: string, phaseContent?: Content) => void;

  // Estado de pestañas
  activeTab: string;
  setActiveTab: (tab: string) => void;
  handleTabChange: (tab: string) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  children: ReactNode;
  initialProject: Project | null;
  // initialProjectId: number  | null;
}

export const ProjectProvider = ({ children, initialProject }: ProjectProviderProps) => {
  // Estado principal del proyecto
  const [project, setProjectState] = useState<Project | null>(initialProject);
  const [isProjectInitialized, setIsProjectInitialized] = useState<boolean>(false);

  // Determinar ID del proyecto (fallback a 0 si es null)
  const projectId = useMemo(() => project?.id_project ?? null, [project]);

  // Estado de mejora continua (persistido en localStorage)
  const [isContinuousImprovement, setIsContinuousImprovement] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("isContinuousImprovement") === "true";
    }
    return false;
  });

  // Persistencia de pestañas en sessionStorage
  const { get, set } = useStorage(StorageType.Session);
  const getStorageKey = useCallback((id: number) => `p[${id}]-active-tab`, []);

  const [activeTab, setActiveTab] = useState<string>(() => {
    return get<string>(getStorageKey(projectId ?? 0)) ?? "general";
  });
  const previousTabRef = useRef<string | null>(null);
  const cooldownRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab((prev) => {
      if (newTab !== prev && !cooldownRef.current) {
        previousTabRef.current = prev;
        set(getStorageKey(projectId ?? 0), newTab);
        cooldownRef.current = true;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          timeoutRef.current = null;
        }, 500);
        window.dispatchEvent(new CustomEvent("tabChange", {
          detail: { previousTab: prev, activeTab: newTab }
        }));
        return newTab;
      }
      return prev;
    });
  }, [set, getStorageKey, projectId]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Sincronizar activeTab al cambiar projectId
  useEffect(() => {
    const cached = get<string>(getStorageKey(projectId ?? 0)) ?? "general";
    setActiveTab(cached);
    set(getStorageKey(projectId ?? 0), cached);
  }, [projectId, get, set, getStorageKey]);

  // Hook de fases y sugerencias
  const { selectedPhase, setSelectedPhase } = usePhase({ projectId });
  const { data: suggestions = [] } = useFetchSuggestions(projectId, "all");

  // Inicialización desde localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedImprovement = localStorage.getItem("isContinuousImprovement");
      if (storedImprovement !== null) {
        setIsContinuousImprovement(storedImprovement === "true");
      }
      setIsProjectInitialized(true);
    }
  }, []);

  // Setter de proyecto que sincroniza mejora continua
  const setProject = useCallback((newProject: Project | null) => {
    setProjectState((prev) => {
      if (newProject) {
        const flag = newProject.is_continuous_improvement ?? false;
        setIsContinuousImprovement(flag);
        localStorage.setItem("isContinuousImprovement", String(flag));
      }
      return newProject;
    });
  }, []);

  // Actualizar contenido de fase en el proyecto
  const updatePhaseContent = useCallback((phaseName: string, phaseContent?: Content) => {
    if (!project) return;
    const updated: Project = {
      ...project,
      [phaseName]: JSON.stringify(phaseContent)
    };
    setProject(updated);
  }, [project, setProject]);

  return (
    <ProjectContext.Provider
      value={{
        project,
        setProject,
        selectedPhase,
        setSelectedPhase,
        isContinuousImprovement,
        isProjectInitialized,
        suggestions,
        updatePhaseContent,
        activeTab,
        setActiveTab,
        handleTabChange
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject debe ser usado dentro de un ProjectProvider");
  }
  return context;
};
